import { Link } from "react-router-dom";
import {
  Zap, ArrowRight, CheckCircle, UserPlus, Briefcase, Phone, Settings, FileText, Bell,
} from "lucide-react";

import "../styles/marketing.css";
import Seo, { breadcrumbJsonLd } from "../components/marketing/Seo";
import MarketingNav from "../components/marketing/MarketingNav";
import MarketingFooter from "../components/marketing/MarketingFooter";
import Reveal from "../components/marketing/Reveal";

const steps = [
  {
    step: 1,
    icon: UserPlus,
    title: "Create Your Account",
    time: "1 min",
    description: "Sign up with your email and business name. No credit card required — you get a full 7-day free trial.",
    details: [
      "Go to fieldzai.com/register",
      "Enter your email, password, and business name",
      "Verify your email address",
      "You'll be redirected to your new dashboard",
    ],
  },
  {
    step: 2,
    icon: Settings,
    title: "Configure Your Business",
    time: "2 min",
    description: "Set up your business profile with your company info, timezone, and service area.",
    details: [
      "Navigate to Settings from the sidebar",
      "Fill in your business name, address, and phone",
      "Upload your company logo",
      "Set your timezone and business hours",
    ],
  },
  {
    step: 3,
    icon: UserPlus,
    title: "Add Your Team",
    time: "2 min",
    description: "Invite your technicians and dispatchers. They'll receive an SMS with setup instructions.",
    details: [
      "Go to the Team page",
      "Click 'Add Member' and fill in their details",
      "Set their role (admin, dispatcher, or technician)",
      "For technicians: set commission rate and specialties",
      "They'll receive an SMS invite automatically",
    ],
  },
  {
    step: 4,
    icon: Briefcase,
    title: "Create Your First Job",
    time: "2 min",
    description: "Create a job, assign a technician, and schedule it. Your tech gets notified instantly.",
    details: [
      "Click '+New Job' on the Jobs page",
      "Add customer info (name, phone, address)",
      "Select service type and set pricing",
      "Choose a date/time and assign a technician",
      "Save — the technician gets an SMS notification",
    ],
  },
  {
    step: 5,
    icon: Phone,
    title: "Set Up Your Phone (Optional)",
    time: "2 min",
    description: "Buy a virtual phone number for professional SMS and call handling.",
    details: [
      "Go to Settings > Phones",
      "Click 'Buy Number' and search by area code",
      "Purchase a number — it's activated instantly",
      "Configure call forwarding or IVR as needed",
    ],
  },
  {
    step: 6,
    icon: Bell,
    title: "Enable Automations (Optional)",
    time: "1 min",
    description: "Set up automatic notifications for job assignments, reminders, and status changes.",
    details: [
      "Go to Fieldz AI > Automations",
      "Enable job assignment notifications",
      "Set up appointment reminders (e.g., 4 hours before)",
      "Configure review request messages",
    ],
  },
];

const QuickStartPage = () => {
  return (
    <div className="mk min-h-screen overflow-x-hidden" data-testid="quick-start-page-root">
      <Seo
        title="Quick Start Guide — Set Up Fieldz AI in 10 Minutes"
        description="Get your field service business running on Fieldz AI in under 10 minutes: account setup, team invites, first job, phone number, and automations."
        path="/quick-start"
        jsonLd={breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "Quick Start", path: "/quick-start" }])}
      />
      <MarketingNav />

      <section className="pt-28 md:pt-36 pb-10 bg-gradient-to-b from-blue-50/70 via-white to-white text-center px-4 sm:px-6 lg:px-8">
        <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[var(--mk-blue)] mb-5 flex items-center justify-center gap-1.5">
          <Zap className="w-3.5 h-3.5" /> Quick Start
        </p>
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.1] mb-4">Quick Start Guide</h1>
        <p className="text-lg text-slate-600 max-w-xl mx-auto">Get up and running with Fieldz AI in under 10 minutes</p>
        <div className="mt-4 flex items-center justify-center gap-2 text-sm text-slate-500">
          <CheckCircle className="w-4 h-4 text-emerald-600" />
          <span>6 steps</span>
          <span className="mx-1">·</span>
          <span>~10 minutes total</span>
        </div>
      </section>

      <section className="pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto space-y-5">
          {steps.map((s, i) => (
            <Reveal key={i}>
              <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-[0_6px_20px_-8px_rgba(15,23,42,0.08)]" data-testid={`step-${s.step}`}>
                <div className="flex items-center gap-4 p-5 border-b border-slate-100 bg-slate-50/60">
                  <div className="w-10 h-10 rounded-full bg-[var(--mk-blue-light)] flex items-center justify-center shrink-0">
                    <span className="text-[var(--mk-blue)] font-bold text-sm">{s.step}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-900">{s.title}</h3>
                    <p className="text-slate-500 text-xs">{s.description}</p>
                  </div>
                  <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full border border-slate-200 text-slate-500">{s.time}</span>
                </div>
                <div className="p-5">
                  <ul className="space-y-2">
                    {s.details.map((d, j) => (
                      <li key={j} className="flex items-start gap-2.5 text-sm text-slate-600">
                        <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                        <span>{d}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>
          ))}

          <Reveal>
            <div className="rounded-2xl border border-blue-200 bg-gradient-to-b from-blue-50/70 to-white p-7 text-center shadow-[0_10px_30px_-8px_rgba(0,70,190,0.12)]">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Ready to get started?</h3>
              <p className="text-slate-600 text-sm mb-5">Create your free account and follow these steps to set up your business.</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/register" className="mk-btn mk-btn-primary" data-testid="quickstart-trial-btn">
                  <Zap className="w-4 h-4" /> Start Free Trial
                </Link>
                <Link to="/docs" className="mk-btn mk-btn-outline" data-testid="quickstart-docs-btn">
                  <FileText className="w-4 h-4" /> Full Documentation
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default QuickStartPage;
